import type { GetDevicesOptions } from '@bymason/controller-api-hooks';
import {
  getGridBooleanOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {
  ControllerGridFilterInputBoolean,
  ControllerGridFilterInputValue,
} from '@bymason-private/legacy-pattern-components';
import { DEFAULT_PAGE_SIZE } from '../../app-constants';
import { retreiveGridColumnState } from '../../browser-storage';

export enum DevicesListActionTypes {
  Filter = 'device-grid-filter',
  Sort = 'device-grid-sort',
}

interface FilterOptionsActions {
  type: DevicesListActionTypes.Filter;
  filterPayload: GridFilterModel;
}
interface SortOptionsActions {
  type: DevicesListActionTypes.Sort;
  sortPayload: GridSortModel;
}
type DevicesOptionsActions = FilterOptionsActions | SortOptionsActions;

export const deviceListOptionsReducer = (options: GetDevicesOptions, actions: DevicesOptionsActions) => {
  const { limit = DEFAULT_PAGE_SIZE, orderByCol, orderDir, ...filterOptions } = options;

  switch (actions.type) {
    case DevicesListActionTypes.Filter: {
      // remove and rebuild filter options from filterPayload
      const filterInit: GetDevicesOptions = Object.entries({
        limit,
        orderByCol,
        orderDir,
      }).reduce<GetDevicesOptions>(
        (acc, [key, val]) => (val !== undefined ? { ...acc, [key]: val } : acc),
        {}
      );
      return actions.filterPayload.items.reduce<GetDevicesOptions>((acc, item) => {
        const { columnField, value, operatorValue } = item;

        if (value !== undefined && value !== null) {
          switch (columnField) {
            case 'name':
            case 'imei':
            case 'serial':
            case 'all':
              return columnField && value
                ? {
                    ...acc,
                    searchType: operatorValue === 'equals' ? 'exact' : 'partial',
                    type: columnField,
                    searchQuery: value,
                  }
                : acc;
            case 'battery':
              return { ...acc, maxBatteryPercent: value };

            default:
              return columnField ? { ...acc, [columnField]: value } : acc;
          }
        }
        return acc;
      }, filterInit);
    }

    case DevicesListActionTypes.Sort: {
      // remove and rebuild sort options from sortPayload
      const sortInit: GetDevicesOptions = { limit, ...filterOptions };
      return actions.sortPayload.reduce<GetDevicesOptions>((acc, item) => {
        const { field, sort } = item;

        switch (field) {
          case 'createdAt':
          case 'lastHeartbeat':
            return sort !== null ? { ...acc, orderByCol: field, orderDir: sort } : acc;

          default:
            return acc;
        }
      }, sortInit);
    }

    default:
      return { limit: DEFAULT_PAGE_SIZE, ...filterOptions };
  }
};

export const getOptionsFilterModelSortModelFromUrl = (url: URL) => {
  const search = new URLSearchParams(url.search);
  const filterModel: GridFilterModel = { items: [] };
  let sortModel: GridSortModel = [];

  // do not allow unknown filter search keys and values
  // these enforce type literals when ingesting search params
  const allowedSearchCol = ['imei', 'name', 'serial', 'all'] as const;
  const allowedSearchType = ['exact', 'partial'] as const;
  const allowedOrderDir = ['asc', 'desc'] as const;
  const allowedOrderByCol = ['createdAt', 'lastHeartbeat'] as const;

  const initialOptions: GetDevicesOptions = {
    limit: DEFAULT_PAGE_SIZE,
    type: allowedSearchCol.find((col) => search.get('type') === col),
    searchQuery: search.get('searchQuery') ?? undefined,
    searchType: allowedSearchType.find((type) => search.get('searchType') === type),
    orderByCol: allowedOrderByCol.find((col) => search.get('orderByCol') === col),
    orderDir: allowedOrderDir.find((dir) => search.get('orderDir') === dir),
  };
  const explicitFilterOptions: GetDevicesOptions = {
    deviceGroup: search.get('group') ?? search.get('deviceGroup') ?? undefined,
    deviceGroupID: search.get('deviceGroupID') ?? undefined,
    appsUpdated:
      search.get('appsUpdated') !== null ? search.get('appsUpdated') === 'true' || false : undefined,
    osUpdated: search.get('osUpdated') !== null ? search.get('osUpdated') === 'true' || false : undefined,
    projectUpdated:
      search.get('projectUpdated') !== null ? search.get('projectUpdated') === 'true' || false : undefined,
    provisioned:
      search.get('provisioned') !== null ? search.get('provisioned') === 'true' || false : undefined,
    maxBatteryPercent:
      search.get('maxBatteryPercent') !== null ? parseInt(search.get('maxBatteryPercent') || '') : undefined,
    uuid: search.get('deviceGroupID') ?? undefined,
  };

  (Object.keys(initialOptions) as Array<keyof GetDevicesOptions>).forEach((key) => {
    if (initialOptions[key] === undefined) {
      delete initialOptions[key];
    }
  });
  (Object.keys(explicitFilterOptions) as Array<keyof GetDevicesOptions>).forEach((key) => {
    if (explicitFilterOptions[key] === undefined) {
      delete explicitFilterOptions[key];
    }
  });

  // make filter model
  if (initialOptions.type && initialOptions.searchQuery) {
    filterModel.items = [
      ...filterModel.items,
      {
        id: 0,
        columnField: initialOptions.type,
        operatorValue: initialOptions.searchType === 'exact' ? 'equals' : 'contains',
        value: initialOptions.searchQuery,
      },
    ];
  }

  Object.entries(explicitFilterOptions).forEach(([key, value], idx) => {
    if (key === 'maxBatteryPercent') {
      filterModel.items = [
        ...filterModel.items,
        { id: idx + 1, columnField: 'battery', operatorValue: '<=', value },
      ];
    } else {
      filterModel.items = [
        ...filterModel.items,
        { id: idx + 1, columnField: key, operatorValue: typeof value === 'boolean' ? 'is' : 'equals', value },
      ];
    }
  });

  // make sort model
  if (initialOptions.orderByCol && initialOptions.orderDir) {
    sortModel = [...sortModel, { field: initialOptions.orderByCol, sort: initialOptions.orderDir }];
  }

  return { filterModel, sortModel, options: { ...initialOptions, ...explicitFilterOptions } };
};

export const groupOperator = getGridStringOperators()
  .filter(({ value }) => ['equals'].includes(value))
  .map((operator) => {
    return {
      ...operator,
      InputComponent: ControllerGridFilterInputValue,
      InputComponentProps: {
        type: 'singleSelect',
      },
    };
  });

export const searchOperator = getGridStringOperators()
  .filter(({ value }) => ['contains', 'equals'].includes(value))
  .map((operator) => {
    return {
      ...operator,
      InputComponent: ControllerGridFilterInputValue,
    };
  });

export const booleanOperator = getGridBooleanOperators().map((operator) => {
  return {
    ...operator,
    InputComponent: ControllerGridFilterInputBoolean,
  };
});

export const mergePersistedColDefs = (initialColDef: GridColDef[]) => {
  // sort and merge in persisted column state into column def
  const persistedColState = retreiveGridColumnState();

  return initialColDef
    .sort((a, b) => {
      return persistedColState[a.field]?.position - persistedColState[b.field]?.position;
    })
    .map((col) => {
      const { field, position, flex, ...persisted } = persistedColState[col.field] || {};
      return { ...col, ...persisted, flex: flex || undefined };
    });
};
